<script setup>
import { computed } from 'vue'
import { useUploads } from './useUploads'
import { formatSize } from './format'

// The upload manager's list, floating over the page: progress, pause, resume, cancel and retry per file
const { state, pause, resume, cancel, retry, clearFinished } = useUploads()

const active = computed(() => state.items.filter(item => !['done', 'skipped', 'cancelled'].includes(item.status)).length)
const statusLabel = item => ({
    queued: 'documents.upload_queued',
    hashing: 'documents.upload_checking',
    starting: 'documents.upload_checking',
    uploading: item.paused ? 'documents.upload_paused' : 'documents.upload_sending',
    committing: 'documents.upload_saving',
    done: 'documents.upload_done',
    skipped: 'documents.upload_skipped',
    cancelled: 'documents.upload_cancelled',
    error: 'documents.upload_failed',
}[item.status])
</script>

<template>
    <div v-if="state.items.length" class="upload-panel" :class="{ collapsed: !state.panelOpen }">
        <div class="upload-panel-header" @click="state.panelOpen = !state.panelOpen">
            <i class="pi pi-upload"></i>
            <span class="flex-1">{{ active ? $t('documents.uploads_active', { count: active }) : $t('documents.uploads_finished') }}</span>
            <Button v-if="!active" icon="pi pi-times" text rounded size="small" @click.stop="clearFinished" v-tooltip="$t('documents.uploads_clear')" />
            <i :class="state.panelOpen ? 'pi pi-chevron-down' : 'pi pi-chevron-up'"></i>
        </div>
        <div v-if="state.panelOpen" class="upload-panel-list">
            <div v-for="item in state.items" :key="item.id" class="upload-item" :data-status="item.status">
                <div class="upload-item-line">
                    <span class="upload-item-name" :title="item.name">{{ item.name }}</span>
                    <span class="upload-item-size">{{ formatSize(item.size) }}</span>
                    <Button v-if="item.status === 'uploading' && !item.paused" icon="pi pi-pause" text rounded size="small" @click="pause(item)" v-tooltip="$t('documents.upload_pause')" />
                    <Button v-if="item.status === 'uploading' && item.paused" icon="pi pi-play" text rounded size="small" @click="resume(item)" v-tooltip="$t('documents.upload_resume')" />
                    <Button v-if="item.status === 'error'" icon="pi pi-refresh" text rounded size="small" @click="retry(item)" v-tooltip="$t('documents.upload_retry')" />
                    <Button v-if="!['done', 'skipped', 'cancelled'].includes(item.status)" icon="pi pi-times" text rounded size="small" severity="secondary" @click="cancel(item)" v-tooltip="$t('core.cancel')" />
                </div>
                <ProgressBar :value="Math.round(item.progress * 100)" :show-value="false" style="height: 4px" />
                <small class="upload-item-status">{{ $t(statusLabel(item)) }}<template v-if="item.error">: {{ item.error }}</template></small>
            </div>
        </div>
    </div>
</template>

<style scoped>
.upload-panel {
    position: fixed;
    right: 1.5rem;
    bottom: calc(var(--app-tabbar-height, 0px) + 1.5rem);
    width: 360px;
    max-width: calc(100vw - 2rem);
    background: var(--p-surface-0);
    border: 1px solid var(--p-surface-200);
    border-radius: 10px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    z-index: 1000;
}

.upload-panel-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.6rem 0.9rem;
    cursor: pointer;
    font-weight: 600;
}

.upload-panel-list {
    max-height: 320px;
    overflow-y: auto;
    border-top: 1px solid var(--p-surface-200);
}

.upload-item {
    padding: 0.5rem 0.9rem;
    border-bottom: 1px solid var(--p-surface-100);
}

.upload-item-line {
    display: flex;
    align-items: center;
    gap: 0.25rem;
}

.upload-item-name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.upload-item-size,
.upload-item-status {
    color: var(--p-text-muted-color);
    font-size: 0.8rem;
}

.upload-item[data-status='error'] .upload-item-status {
    color: var(--p-red-500);
}
</style>
